
import React from 'react';
import { EmailSummaryForHook } from "./types";
import { ImprovedEmailCard } from "./ImprovedEmailCard";
import { EmailSummarySkeleton } from "../EmailSummarySkeleton";
import { RefreshCw, Inbox } from "lucide-react";

interface EnhancedEmailListProps {
  emailSummaries: EmailSummaryForHook[];
  isFetchingEmails: boolean;
  isProcessing: boolean;
  archiveEmailMutation: {
    isPending: boolean;
    variables?: string;
  };
  flagEmailMutation: {
    isPending: boolean;
    variables?: { emailId: string; currentFlagStatus: boolean };
  };
  onArchiveEmail: (emailId: string) => void;
  onFlagEmail: (emailId: string, currentFlagStatus: boolean) => void;
  emailsToProcessPerRequest: number;
}

export const EnhancedEmailList = ({
  emailSummaries,
  isFetchingEmails,
  isProcessing,
  archiveEmailMutation,
  flagEmailMutation,
  onArchiveEmail,
  onFlagEmail,
  emailsToProcessPerRequest,
}: EnhancedEmailListProps) => {
  // Empty state - loading
  if (isFetchingEmails && emailSummaries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-center bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
        <RefreshCw className="h-12 w-12 text-slate-400 animate-spin mb-4" />
        <p className="text-slate-600 text-lg font-medium">Loading your emails...</p>
        <p className="text-slate-500 text-sm mt-1">This may take a moment</p>
      </div>
    );
  }

  // Empty state - no emails
  if (!isFetchingEmails && !isProcessing && emailSummaries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-center bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
        <Inbox className="h-12 w-12 text-slate-400 mb-4" />
        <p className="text-slate-600 text-lg font-medium">No emails found</p>
        <p className="text-slate-500 text-sm mt-1">
          Click "Process New Emails" to analyze your inbox
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Show skeletons when processing */}
      {(isProcessing || archiveEmailMutation.isPending || flagEmailMutation.isPending) && emailSummaries.length > 0 && (
        <div className="space-y-4">
          {Array.from({ length: Math.min(emailSummaries.length, emailsToProcessPerRequest) }).map((_, index) => (
            <EmailSummarySkeleton key={`skeleton-processing-${index}`} />
          ))}
        </div>
      )}
      
      {/* Show actual emails using improved cards */}
      <div className="space-y-4">
        {emailSummaries.map((email) => (
          <ImprovedEmailCard 
            key={email.id}
            email={email}
            onArchive={onArchiveEmail}
            onFlag={onFlagEmail}
            isArchiving={archiveEmailMutation.isPending && archiveEmailMutation.variables === email.id}
            isFlagging={flagEmailMutation.isPending && flagEmailMutation.variables?.emailId === email.id}
          />
        ))}
      </div>
    </div>
  );
};
